import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Combobox,
  createListCollection,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Portal,
  Spinner,
  Table,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import {
  Check,
  ChevronsUpDown,
  Pencil,
  Plus,
  Star,
  Trash2,
  Users,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ConfirmDialog,
  copyToClipboard,
  ListToolbar,
  LoadingState,
  SortableColumnHeader,
  type ToastLike,
  type TranslateFn,
} from '@mangovoice/mango-ui'

import type { UserApi } from '../api/userApi'
import {
  RemoveUserError,
  useRemoveUserFromPbx,
} from '../hooks/useUserMutations'
import { useGetUser, useListUsers } from '../hooks/useUsers'
import type { PbxUser, UserListItem } from '../types/user.types'
import { createClientNavigationClickHandler } from './linkNavigation'

/**
 * Page size for the reassignment-candidate search. Narrowing happens
 * server-side via the `search` param, so this only bounds how many matches a
 * single query renders — not which users are reachable.
 */
const CANDIDATE_PAGE_SIZE = 50

// ─── Sorting ────────────────────────────────────────────────────────────────

type SortDirection = 'asc' | 'desc'

interface SortState {
  field: string
  direction: SortDirection
}

interface SortableColumn {
  /** Stable identifier used as a React key; matches the i18n key for the column. */
  id: string
  label: string
  field: string | null
}

interface SortableColumnDef {
  labelKey: string
  defaultLabel: string
  field: string | null
}

const SORTABLE_COLUMN_DEFS: SortableColumnDef[] = [
  { labelKey: 'users.list.column.name', defaultLabel: 'Name', field: 'name' },
  {
    labelKey: 'users.list.column.usernameOrEmail',
    defaultLabel: 'Username / Email',
    field: 'username',
  },
  { labelKey: 'users.list.column.role', defaultLabel: 'Role', field: null },
  {
    labelKey: 'users.list.column.faxEmail',
    defaultLabel: 'Fax Email',
    field: null,
  },
  {
    labelKey: 'users.list.column.lastLogin',
    defaultLabel: 'Last Login',
    field: 'last_sign_in_at',
  },
  {
    labelKey: 'users.list.column.lastUpdated',
    defaultLabel: 'Last Updated',
    field: 'updated_at',
  },
  { labelKey: 'users.list.column.action', defaultLabel: 'Action', field: null },
]

const resolveSortableColumns = (
  t?: TranslateFn,
  includeActionColumn = true,
): SortableColumn[] =>
  SORTABLE_COLUMN_DEFS.filter(
    ({ labelKey }) =>
      includeActionColumn || labelKey !== 'users.list.column.action',
  ).map(({ labelKey, defaultLabel, field }) => ({
    id: labelKey,
    label: t ? t(labelKey, { defaultValue: defaultLabel }) : defaultLabel,
    field,
  }))

const DEFAULT_SORT: SortState = { field: 'name', direction: 'asc' }

function toOrderingParam(sort: SortState): string | undefined {
  if (!sort.field) return undefined
  return sort.direction === 'desc' ? `-${sort.field}` : sort.field
}

export interface UserListProps {
  api: UserApi
  pbxId: number
  onNavigateToUser: (userId: number) => void
  getUserDetailHref?: (userId: number) => string
  onNavigateToNew: () => void
  canCreate: boolean
  hideCreateButton?: boolean
  canDelete: boolean
  /**
   * When true, renders a pencil "Edit" icon on each row alongside the delete
   * button. The action column is shown when either canEdit or canDelete is
   * enabled.
   */
  canEdit?: boolean
  /** Invoked when the row-level Edit icon is clicked. */
  onEditClick?: (userId: number) => void
  /** Optional href for the Edit icon so it renders as a real `<a>`. */
  getUserEditHref?: (userId: number) => string
  defaultPageSize?: number
  toast?: ToastLike
  /** Optional translation function. When omitted, falls back to English defaults. */
  t?: TranslateFn
  /**
   * i18n key prefix for the pagination toolbar (e.g. `'users.list.pagination'`).
   * Forwarded to `ListToolbar`. When omitted, `ListToolbar`'s default prefix
   * (`'pagination'`) is used; combined with a missing `t`, all toolbar strings
   * fall back to English.
   */
  paginationI18nPrefix?: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'N/A'
  }
}

const getUserDisplayName = (user: UserListItem): string =>
  user.name || `${user.first_name} ${user.last_name}`.trim()

const getRoleBadgeStyles = (role: string | undefined | null) => {
  const value = (role || '').toLowerCase()
  if (value.includes('super')) return { bg: 'blue.100', color: 'blue.700' }
  if (value.includes('senior')) return { bg: 'cyan.100', color: 'cyan.700' }
  if (value.includes('admin')) return { bg: 'purple.100', color: 'purple.700' }
  if (value.includes('unrestricted'))
    return { bg: 'green.100', color: 'green.700' }
  if (value.includes('restricted')) return { bg: 'red.100', color: 'red.700' }
  return { bg: 'gray.100', color: 'gray.700' }
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const CopyableText: React.FC<{
  value: string | undefined | null
  color?: string
  t?: TranslateFn
}> = ({ value, color = 'fg.default', t }) => {
  const [copied, setCopied] = useState(false)

  if (!value) {
    return (
      <Text fontSize="sm" color="fg.muted">
        —
      </Text>
    )
  }

  const copiedLabel = t
    ? t('users.list.copied', { defaultValue: 'Copied!' })
    : 'Copied!'
  const clickToCopyLabel = t
    ? t('users.list.clickToCopy', { defaultValue: 'Click to copy' })
    : 'Click to copy'

  // The value itself is the control and swaps to "Copied!" — a different look
  // than CopyButton's icon, so this stays a local button on copyToClipboard.
  // Stop propagation so the enclosing row click handler doesn't treat a copy
  // click as a navigation intent, in case Chakra ever renders `as="button"` as
  // a non-button element and bypasses the `closest('button, a')` guard.
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await copyToClipboard(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Text
      as="button"
      fontSize="sm"
      color={copied ? 'fg.success' : color}
      cursor="pointer"
      background="none"
      border="none"
      p={0}
      textAlign="left"
      _hover={{ textDecoration: 'underline' }}
      onClick={handleCopy}
      title={copied ? copiedLabel : clickToCopyLabel}
    >
      {copied ? copiedLabel : value}
    </Text>
  )
}

const UsersLoadingState: React.FC<{ t?: TranslateFn }> = ({ t }) => (
  <LoadingState
    layout="alert"
    title={
      t
        ? t('users.list.loading', { defaultValue: 'Loading users...' })
        : 'Loading users...'
    }
  />
)

const ErrorState: React.FC<{ message: string; t?: TranslateFn }> = ({
  message,
  t,
}) => (
  <Alert.Root status="error" p={4}>
    <Alert.Indicator />
    <Box>
      <Text fontWeight="bold">
        {t ? t('users.list.error', { defaultValue: 'Error' }) : 'Error'}
      </Text>
      <Text>{message}</Text>
    </Box>
  </Alert.Root>
)

const EmptyState: React.FC<{ hasSearchTerm?: boolean; t?: TranslateFn }> = ({
  hasSearchTerm = false,
  t,
}) => {
  const title = hasSearchTerm
    ? t
      ? t('users.list.emptyNoResultsTitle', {
          defaultValue: 'No results found',
        })
      : 'No results found'
    : t
      ? t('users.list.emptyNoUsersTitle', { defaultValue: 'No users yet' })
      : 'No users yet'
  const description = hasSearchTerm
    ? t
      ? t('users.list.emptyNoResultsDescription', {
          defaultValue: 'Try adjusting your search terms',
        })
      : 'Try adjusting your search terms'
    : t
      ? t('users.list.emptyNoUsersDescription', {
          defaultValue: 'Get started by creating your first user',
        })
      : 'Get started by creating your first user'

  return (
    <VStack
      w="100%"
      justifyContent="center"
      alignItems="center"
      gap={4}
      py={16}
    >
      <Icon boxSize={16} color={hasSearchTerm ? 'fg.muted' : 'blue.solid'}>
        {hasSearchTerm ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
        ) : (
          <Users />
        )}
      </Icon>
      <VStack gap={1}>
        <Text fontWeight="semibold" fontSize="lg">
          {title}
        </Text>
        <Text color="fg.muted" fontSize="sm">
          {description}
        </Text>
      </VStack>
    </VStack>
  )
}

const SearchInput: React.FC<{
  value: string
  onChange: (v: string) => void
  t?: TranslateFn
}> = ({ value, onChange, t }) => (
  <Box w="307px" position="relative">
    <Icon
      boxSize={4}
      color="fg.muted"
      position="absolute"
      left={3}
      top="50%"
      transform="translateY(-50%)"
      zIndex={1}
      pointerEvents="none"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    </Icon>
    <Input
      placeholder={
        t
          ? t('users.list.searchPlaceholder', {
              defaultValue: 'Search by name, username, or email...',
            })
          : 'Search by name, username, or email...'
      }
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="md"
      pl={10}
      bg="bg.subtle"
      borderColor="border.default"
      borderRadius="lg"
      _placeholder={{ color: 'fg.subtle' }}
      _hover={{ borderColor: 'border.emphasized' }}
      _focusVisible={{
        borderColor: 'brand.solid',
        boxShadow: '0 0 0 2px var(--chakra-colors-brand-solid)',
        outline: 'none',
      }}
      aria-label={
        t
          ? t('users.list.searchAriaLabel', { defaultValue: 'Search users' })
          : 'Search users'
      }
    />
  </Box>
)

interface UserEditActionProps {
  userId: number
  displayName: string
  editHref: string | undefined
  onEditClick: ((userId: number) => void) | undefined
}

/**
 * Renders the pencil "Edit" icon for a user row. When `editHref` is provided,
 * this renders as a real anchor so middle-click / open-in-new-tab work; an
 * optional `onEditClick` callback enables client-side navigation on plain
 * clicks. When no href is provided, falls back to a button that requires
 * `onEditClick` to do anything useful.
 */
const UserEditAction: React.FC<UserEditActionProps> = ({
  userId,
  displayName,
  editHref,
  onEditClick,
}) => {
  const editLabel = `Edit ${displayName || 'user'}`
  const focusVisibleStyles = {
    ring: '2px',
    ringColor: 'blue.500',
    ringOffset: '2px',
  }

  if (editHref) {
    // Only intercept the click when we actually have a client-side navigation
    // callback. Otherwise leave the anchor's default navigation intact so the
    // link doesn't silently swallow `preventDefault()`.
    const handleClick = onEditClick
      ? createClientNavigationClickHandler(() => onEditClick(userId))
      : undefined
    return (
      <IconButton
        asChild
        size="xs"
        colorPalette="neutral"
        variant="subtle"
        aria-label={editLabel}
        _focusVisible={focusVisibleStyles}
      >
        <a href={editHref} onClick={handleClick}>
          <Pencil size={16} />
        </a>
      </IconButton>
    )
  }

  return (
    <IconButton
      size="xs"
      colorPalette="neutral"
      variant="subtle"
      onClick={() => onEditClick?.(userId)}
      aria-label={editLabel}
      _focusVisible={focusVisibleStyles}
    >
      <Pencil size={16} />
    </IconButton>
  )
}

interface UserRowProps {
  pbxUser: PbxUser
  onNavigateToUser: (userId: number) => void
  getUserDetailHref?: (userId: number) => string
  onDeleteClick: (id: number, name: string) => void
  canDelete: boolean
  canEdit: boolean
  onEditClick?: (userId: number) => void
  getUserEditHref?: (userId: number) => string
  t?: TranslateFn
}

const UserRow: React.FC<UserRowProps> = ({
  pbxUser,
  onNavigateToUser,
  getUserDetailHref,
  onDeleteClick,
  canDelete,
  canEdit,
  onEditClick,
  getUserEditHref,
  t,
}) => {
  const { user, sending_fax_email } = pbxUser
  const displayName = getUserDisplayName(user)
  const userDetailHref = getUserDetailHref?.(user.id)
  const handleUserLinkClick = createClientNavigationClickHandler(() =>
    onNavigateToUser(user.id),
  )

  return (
    // Row click is a mouse-only convenience — no ARIA role or tabIndex is set
    // so screen readers still see the row as a normal <tr> and can reach the
    // nested interactive elements (Name navigation button, action icons,
    // CopyableText copy button). Keyboard/SR users activate navigation via
    // the Name button.
    <Table.Row
      cursor="pointer"
      // Clicks that originate on a nested button/link (action icons,
      // CopyableText) handle themselves — don't hijack them for row
      // navigation.
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) {
          return
        }
        onNavigateToUser(user.id)
      }}
      _hover={{ bg: 'bg.subtle' }}
      _focusWithin={{
        bg: 'bg.subtle',
      }}
    >
      <Table.Cell px={4} py={3}>
        <HStack gap={2}>
          <Box
            w={6}
            h={6}
            borderRadius="full"
            bg="bg.muted"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="fg.muted"
            flexShrink={0}
          >
            <Users size={12} />
          </Box>
          {userDetailHref ? (
            <Text
              asChild
              fontWeight="medium"
              color="fg.info"
              _hover={{ textDecoration: 'underline' }}
              _focusVisible={{
                textDecoration: 'underline',
                outline: '2px solid',
                outlineColor: 'blue.500',
                outlineOffset: '2px',
                borderRadius: 'sm',
              }}
              cursor="pointer"
              p={0}
            >
              <a href={userDetailHref} onClick={handleUserLinkClick}>
                {displayName || 'Unnamed User'}
              </a>
            </Text>
          ) : (
            <Text
              as="button"
              fontWeight="medium"
              color="fg.info"
              _hover={{ textDecoration: 'underline' }}
              _focusVisible={{
                textDecoration: 'underline',
                outline: '2px solid',
                outlineColor: 'blue.500',
                outlineOffset: '2px',
                borderRadius: 'sm',
              }}
              onClick={() => onNavigateToUser(user.id)}
              cursor="pointer"
              background="none"
              border="none"
              p={0}
            >
              {displayName || 'Unnamed User'}
            </Text>
          )}
          {user.is_superuser ? (
            <Icon boxSize={3.5} color="fg.info" aria-label="Superuser">
              <Star fill="currentColor" />
            </Icon>
          ) : user.is_staff ? (
            <Icon boxSize={3.5} color="fg.warning" aria-label="Staff user">
              <Star fill="currentColor" />
            </Icon>
          ) : null}
        </HStack>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <VStack gap={0.5} align="start">
          <CopyableText value={user.username} t={t} />
          <CopyableText value={user.email} color="fg.muted" t={t} />
        </VStack>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Badge
          borderRadius="sm"
          px={2}
          py={1}
          fontWeight="normal"
          fontSize="xs"
          bg={getRoleBadgeStyles(user.role).bg}
          color={getRoleBadgeStyles(user.role).color}
        >
          {user.role ?? '—'}
        </Badge>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {sending_fax_email || '—'}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {user.last_sign_in_at ? formatDate(user.last_sign_in_at) : '—'}
        </Text>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Text fontSize="sm" color="fg.muted">
          {formatDate(user.updated_at)}
        </Text>
      </Table.Cell>
      {(canEdit || canDelete) && (
        <Table.Cell px={4} py={3}>
          <Box
            display="flex"
            gap={2}
            role="group"
            aria-label={`Actions for ${displayName || 'user'}`}
          >
            {canEdit && (
              <UserEditAction
                userId={user.id}
                displayName={displayName}
                editHref={getUserEditHref?.(user.id)}
                onEditClick={onEditClick}
              />
            )}
            {canDelete && (
              <IconButton
                size="xs"
                colorPalette="error"
                variant="subtle"
                onClick={() => onDeleteClick(user.id, displayName)}
                aria-label={`Delete ${displayName || 'user'}`}
                _focusVisible={{
                  ring: '2px',
                  ringColor: 'red.500',
                  ringOffset: '2px',
                }}
              >
                <Trash2 size={16} />
              </IconButton>
            )}
          </Box>
        </Table.Cell>
      )}
    </Table.Row>
  )
}

interface RemoveUserFromPbxDialogProps {
  api: UserApi
  pbxId: number
  isOpen: boolean
  onClose: () => void
  /**
   * Invoked when the user confirms removal. When the user has no assigned
   * devices or extensions on this PBX, `reassignAssetsTo` will be undefined
   * (matching legacy behavior of a direct remove). When the user has assets,
   * it will be the selected target user's id.
   */
  onConfirm: (reassignAssetsTo?: number) => void
  userName: string
  userId: number | null
  isProcessing: boolean
  t?: TranslateFn
}

const RemoveUserFromPbxDialog = React.memo<RemoveUserFromPbxDialogProps>(
  ({
    api,
    pbxId,
    isOpen,
    onClose,
    userName,
    userId,
    onConfirm,
    isProcessing,
    t,
  }) => {
    const [reassignUserId, setReassignUserId] = useState('')

    // Fetch per-PBX asset counts so we can mirror legacy behavior: if the
    // user has no assigned devices/extensions, a simple confirm is enough;
    // otherwise the caller must pick a target user to reassign to.
    const {
      user: userDetail,
      isLoading: isLoadingUser,
      error: userDetailError,
    } = useGetUser({
      api,
      userId: userId ?? 0,
      queryOptions: { enabled: isOpen && !!userId && userId > 0 },
    })

    const { assignedExtensionCount, assignedDeviceCount } = useMemo(() => {
      if (!userDetail) {
        return { assignedExtensionCount: 0, assignedDeviceCount: 0 }
      }
      const association = userDetail.pbx_users.find((pu) => pu.pbx_id === pbxId)
      return {
        assignedExtensionCount: association?.assigned_extension_count ?? 0,
        assignedDeviceCount: association?.assigned_device_count ?? 0,
      }
    }, [userDetail, pbxId])

    // If the detail fetch fails we don't know whether the user has assets, so
    // fall back to the reassignment flow (safe default) and surface an inline
    // warning. This prevents the dialog from hanging on a spinner and avoids
    // silently orphaning assets via a simple remove.
    const hasFetchError = !isLoadingUser && !!userDetailError
    const hasAssets = assignedExtensionCount > 0 || assignedDeviceCount > 0
    const treatAsHasAssets = hasAssets || hasFetchError
    // Counts are "known enough" to render a determinate branch once the detail
    // request has resolved for the current user, or once we've given up after
    // an error.
    const isCountsKnown =
      (!isLoadingUser && userDetail?.id === userId) || hasFetchError

    // The combobox input doubles as the search field and the display of the
    // current selection. Ark's default `replace` selection behavior writes the
    // picked item's label back into the input, so the label must not be treated
    // as a query — otherwise a pick fires `search="Name (email)"`, which matches
    // nothing and collapses the list right after a valid selection.
    const [candidateInput, setCandidateInput] = useState('')
    const [candidateSearch, setCandidateSearch] = useState('')
    const justSelectedRef = React.useRef(false)

    useEffect(() => {
      if (justSelectedRef.current) {
        justSelectedRef.current = false
        return
      }
      const timer = setTimeout(
        () => setCandidateSearch(candidateInput.trim()),
        300,
      )
      return () => clearTimeout(timer)
    }, [candidateInput])

    // Reassignment candidates are searched server-side rather than reusing the
    // table's current page: that page is narrowed by the table's own search and
    // pagination, so removing a user found via search offered no targets at all.
    const {
      pbxUsers: candidateUsers,
      isLoading: isLoadingCandidates,
      isFetching: isFetchingCandidates,
      error: candidateError,
    } = useListUsers({
      api,
      pbxId,
      params: {
        limit: CANDIDATE_PAGE_SIZE,
        ordering: 'name',
        ...(candidateSearch ? { search: candidateSearch } : {}),
      },
      queryOptions: { enabled: isOpen && treatAsHasAssets && isCountsKnown },
    })

    const availableUsers = useMemo(
      () => candidateUsers.filter((pu) => pu.user.id !== userId),
      [candidateUsers, userId],
    )
    // Only an unsearched, non-errored empty result means the PBX truly has no
    // other users; an empty search result just means nothing matched, and a
    // failed fetch tells us nothing at all.
    const hasAvailableUsers = availableUsers.length > 0
    const isCandidateSearchActive = candidateSearch.length > 0
    const hasCandidateError = !!candidateError

    // Remembered separately from the collection: a later search can narrow the
    // results so the selected user is no longer among them, and the input must
    // still be restorable to their label.
    const selectedLabelRef = React.useRef('')

    useEffect(() => {
      if (!isOpen) {
        setReassignUserId('')
        setCandidateInput('')
        setCandidateSearch('')
        justSelectedRef.current = false
        selectedLabelRef.current = ''
      }
    }, [isOpen])

    const candidateCollection = useMemo(
      () =>
        createListCollection({
          items: availableUsers.map((pu) => ({
            value: pu.user.id.toString(),
            label: `${getUserDisplayName(pu.user)} (${pu.user.email})`,
          })),
          itemToValue: (item) => item.value,
          itemToString: (item) => item.label,
        }),
      [availableUsers],
    )

    // Restore the input to the committed selection when the popover closes
    // without a pick, so a leftover query can never disagree with the target
    // that "Reassign & Remove" actually uses.
    const handleCandidateOpenChange = (open: boolean) => {
      if (open) return
      // Same guard as the pick path: restoring the label into the input must not
      // be debounced into a server search for that label.
      justSelectedRef.current = true
      setCandidateInput(selectedLabelRef.current)
      setCandidateSearch('')
    }

    const handleConfirm = () => {
      if (!isCountsKnown) return
      if (treatAsHasAssets) {
        const parsed = parseInt(reassignUserId, 10)
        if (!Number.isFinite(parsed) || parsed <= 0) return
        onConfirm(parsed)
        return
      }
      onConfirm(undefined)
    }

    const fallbackUserLabel = t
      ? t('users.list.defaultUserName', { defaultValue: 'User' })
      : 'User'
    const displayUserName = userName || fallbackUserLabel

    // While loading, render a neutral title/description/confirm label so the
    // copy doesn't visibly flip when the counts resolve.
    const title = !isCountsKnown
      ? t
        ? t('users.list.removeFromPbxNeutralTitle', {
            defaultValue: 'Remove {{name}} from PBX',
            name: displayUserName,
          })
        : `Remove ${displayUserName} from PBX`
      : treatAsHasAssets
        ? t
          ? t('users.list.removeFromPbxTitle', {
              defaultValue: 'Remove {{name}} from PBX',
              name: displayUserName,
            })
          : `Remove ${displayUserName} from PBX`
        : t
          ? t('users.list.removeFromPbxSimpleTitle', {
              defaultValue: 'Remove {{name}} from PBX?',
              name: displayUserName,
            })
          : `Remove ${displayUserName} from PBX?`

    const description = !isCountsKnown
      ? ''
      : treatAsHasAssets
        ? t
          ? t('users.list.removeFromPbxDescription', {
              defaultValue:
                'This user has assigned devices or extensions. They will be reassigned to the selected user.',
            })
          : 'This user has assigned devices or extensions. They will be reassigned to the selected user.'
        : t
          ? t('users.list.removeFromPbxSimpleDescription', {
              defaultValue:
                'This will remove the user from this PBX. Are you sure you want to continue?',
            })
          : 'This will remove the user from this PBX. Are you sure you want to continue?'

    const confirmLabel = !isCountsKnown
      ? t
        ? t('users.list.remove', { defaultValue: 'Remove' })
        : 'Remove'
      : treatAsHasAssets
        ? t
          ? t('users.list.reassignAndRemove', {
              defaultValue: 'Reassign & Remove',
            })
          : 'Reassign & Remove'
        : t
          ? t('users.list.remove', { defaultValue: 'Remove' })
          : 'Remove'

    // Gating on a picked target alone: a search that currently matches nothing
    // must not invalidate a selection the user already made.
    const confirmDisabled =
      !isCountsKnown || (treatAsHasAssets && !reassignUserId)

    return (
      <ConfirmDialog
        open={isOpen}
        onClose={onClose}
        onConfirm={handleConfirm}
        isLoading={isProcessing}
        confirmDisabled={confirmDisabled}
        variant="destructive"
        title={title}
        description={description}
        confirmLabel={confirmLabel}
        cancelLabel={
          t ? t('users.list.cancel', { defaultValue: 'Cancel' }) : 'Cancel'
        }
      >
        {!isCountsKnown ? (
          <HStack mt={4} gap={2} color="fg.muted" aria-live="polite">
            <Spinner size="sm" />
            <Text fontSize="sm">
              {t
                ? t('users.list.checkingAssignedAssets', {
                    defaultValue: 'Checking for assigned assets...',
                  })
                : 'Checking for assigned assets...'}
            </Text>
          </HStack>
        ) : treatAsHasAssets ? (
          <>
            {hasFetchError && (
              <Alert.Root status="warning" size="sm" mt={4}>
                <Alert.Indicator />
                <Alert.Description fontSize="sm">
                  {t
                    ? t('users.list.assetCheckFailed', {
                        defaultValue:
                          'Could not determine if this user has assigned devices or extensions. Please choose a user to reassign any assets to.',
                      })
                    : 'Could not determine if this user has assigned devices or extensions. Please choose a user to reassign any assets to.'}
                </Alert.Description>
              </Alert.Root>
            )}
            {hasAvailableUsers ||
            isCandidateSearchActive ||
            isLoadingCandidates ||
            hasCandidateError ? (
              <Box mt={4}>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {t
                    ? t('users.list.reassignAssetsTo', {
                        defaultValue: 'Reassign assets to',
                      })
                    : 'Reassign assets to'}
                </Text>
                <Combobox.Root
                  collection={candidateCollection}
                  value={reassignUserId ? [reassignUserId] : []}
                  onValueChange={(details) => {
                    // Ark echoes the picked label into the input next; flag it
                    // so the debounce doesn't turn that label into a query.
                    justSelectedRef.current = true
                    const next = details.value[0] ?? ''
                    setReassignUserId(next)
                    selectedLabelRef.current =
                      candidateCollection.items.find(
                        (item) => item.value === next,
                      )?.label ?? ''
                  }}
                  inputValue={candidateInput}
                  onInputValueChange={(details) =>
                    setCandidateInput(details.inputValue)
                  }
                  onOpenChange={(details) =>
                    handleCandidateOpenChange(details.open)
                  }
                  openOnClick
                  size="md"
                  width="100%"
                >
                  <Combobox.Control>
                    <Combobox.Input
                      placeholder={
                        t
                          ? t('users.list.selectUserPlaceholder', {
                              defaultValue: 'Select a user...',
                            })
                          : 'Select a user...'
                      }
                      aria-label={
                        t
                          ? t('users.list.searchUsersToReassign', {
                              defaultValue:
                                'Search users to reassign assets to',
                            })
                          : 'Search users to reassign assets to'
                      }
                      bg="bg.subtle"
                      borderColor="border.default"
                      _hover={{ borderColor: 'border.emphasized' }}
                      onFocus={(e) => {
                        // Select the existing label so typing replaces it
                        // instead of appending to it. Queued through rAF
                        // because Ark's own focus handler would otherwise
                        // reset the cursor on the same tick.
                        const input = e.currentTarget
                        requestAnimationFrame(() => input.select())
                      }}
                    />
                    <Combobox.IndicatorGroup>
                      {isFetchingCandidates && <Spinner size="xs" />}
                      <Combobox.Trigger>
                        <Icon boxSize={4} color="fg.muted">
                          <ChevronsUpDown />
                        </Icon>
                      </Combobox.Trigger>
                    </Combobox.IndicatorGroup>
                  </Combobox.Control>
                  <Portal>
                    <Combobox.Positioner>
                      <Combobox.Content maxH="240px" overflowY="auto">
                        {!hasAvailableUsers && (
                          <Text
                            px={3}
                            py={2}
                            fontSize="sm"
                            color="fg.muted"
                            role="status"
                            aria-live="polite"
                          >
                            {isFetchingCandidates
                              ? t
                                ? t('users.list.searchingUsers', {
                                    defaultValue: 'Searching...',
                                  })
                                : 'Searching...'
                              : hasCandidateError
                                ? t
                                  ? t('users.list.candidateLoadFailed', {
                                      defaultValue:
                                        "Couldn't load users. Check your connection and try again.",
                                    })
                                  : "Couldn't load users. Check your connection and try again."
                                : t
                                  ? t('users.list.noMatchingUsers', {
                                      defaultValue: 'No matching users',
                                    })
                                  : 'No matching users'}
                          </Text>
                        )}
                        {candidateCollection.items.map((item) => (
                          <Combobox.Item key={item.value} item={item}>
                            <Combobox.ItemText>{item.label}</Combobox.ItemText>
                            <Combobox.ItemIndicator>
                              <Icon boxSize={4}>
                                <Check />
                              </Icon>
                            </Combobox.ItemIndicator>
                          </Combobox.Item>
                        ))}
                      </Combobox.Content>
                    </Combobox.Positioner>
                  </Portal>
                </Combobox.Root>
              </Box>
            ) : (
              <Alert.Root status="warning" size="sm" mt={4}>
                <Alert.Indicator />
                <Alert.Description fontSize="sm">
                  {t
                    ? t('users.list.noOtherUsersOnPbx', {
                        defaultValue:
                          'There are no other users on this PBX. Add another user before removing this one so their assets can be reassigned.',
                      })
                    : 'There are no other users on this PBX. Add another user before removing this one so their assets can be reassigned.'}
                </Alert.Description>
              </Alert.Root>
            )}
          </>
        ) : null}
      </ConfirmDialog>
    )
  },
)
RemoveUserFromPbxDialog.displayName = 'RemoveUserFromPbxDialog'

// ─── Main component ────────────────────────────────────────────────────────────

export const UserList: React.FC<UserListProps> = ({
  api,
  pbxId,
  onNavigateToUser,
  getUserDetailHref,
  onNavigateToNew,
  canCreate,
  hideCreateButton = false,
  canDelete,
  canEdit = false,
  onEditClick,
  getUserEditHref,
  defaultPageSize = 25,
  toast,
  t,
  paginationI18nPrefix,
}) => {
  const isValidPbxId = !Number.isNaN(pbxId) && pbxId > 0
  const normalizedDefaultPageSize =
    Number.isFinite(defaultPageSize) && defaultPageSize > 0
      ? Math.floor(defaultPageSize)
      : 25

  const [searchTerm, setSearchTerm] = useState('')
  const [pageSize, setPageSize] = useState<number>(normalizedDefaultPageSize)
  const [currentPage, setCurrentPage] = useState(1)
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [deleteDialogState, setDeleteDialogState] = useState<{
    isOpen: boolean
    userId: number | null
    userName: string
  }>({ isOpen: false, userId: null, userName: '' })
  const normalizedSearchTerm = searchTerm.trim()
  const offset = (currentPage - 1) * pageSize

  const { pbxUsers, pagination, isLoading, error } = useListUsers({
    api,
    pbxId,
    params: {
      limit: pageSize,
      offset,
      ordering: toOrderingParam(sort),
      ...(normalizedSearchTerm ? { search: normalizedSearchTerm } : {}),
    },
    queryOptions: { enabled: isValidPbxId },
  })

  const totalCount = pagination.count
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const pageSizeOptions = useMemo(
    () =>
      Array.from(new Set([normalizedDefaultPageSize, 25, 50, 100])).sort(
        (a, b) => a - b,
      ),
    [normalizedDefaultPageSize],
  )
  const showActionColumn = canEdit || canDelete
  const sortableColumns = useMemo(
    () => resolveSortableColumns(t, showActionColumn),
    [t, showActionColumn],
  )

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const removeUserMutation = useRemoveUserFromPbx({ api, pbxId })

  const handleNewUser = () => {
    if (!isValidPbxId) return
    onNavigateToNew()
  }

  const handleDeleteClick = useCallback((id: number, name: string) => {
    setDeleteDialogState({ isOpen: true, userId: id, userName: name })
  }, [])

  const handleDeleteDialogClose = useCallback(() => {
    setDeleteDialogState({ isOpen: false, userId: null, userName: '' })
  }, [])

  const handleSort = useCallback((field: string, direction: SortDirection) => {
    setCurrentPage(1)
    setSort({ field, direction })
  }, [])

  const handleSearchChange = useCallback((value: string) => {
    setCurrentPage(1)
    setSearchTerm(value)
  }, [])

  const handlePageSizeChange = useCallback((size: number) => {
    setCurrentPage(1)
    setPageSize(size)
  }, [])

  const handleReassignmentConfirm = useCallback(
    (reassignAssetsTo?: number) => {
      if (!deleteDialogState.userId) return

      removeUserMutation.mutate(
        {
          userId: deleteDialogState.userId,
          reassignAssetsTo,
        },
        {
          onSuccess: () => {
            toast?.create({
              title: 'User removed',
              description: `${deleteDialogState.userName || 'User'} has been removed from this PBX.`,
              type: 'success',
              duration: 4000,
            })
            handleDeleteDialogClose()
          },
          onError: (err) => {
            if (err instanceof RemoveUserError && err.code === 'not_found') {
              toast?.create({
                title: 'Already removed',
                description:
                  'This user was not found in the PBX — they may have already been removed.',
                type: 'info',
                duration: 4000,
              })
              handleDeleteDialogClose()
            } else if (
              err instanceof RemoveUserError &&
              err.code === 'forbidden'
            ) {
              toast?.create({
                title: 'Authentication required',
                description:
                  'Your session may have expired. Please re-authenticate and try again.',
                type: 'error',
                duration: 6000,
              })
            } else {
              toast?.create({
                title: 'Error',
                description: err.message || 'Failed to remove user from PBX.',
                type: 'error',
                duration: 5000,
              })
            }
          },
        },
      )
    },
    [deleteDialogState, removeUserMutation, toast, handleDeleteDialogClose],
  )

  const showPagination = totalCount > 0 && !isLoading && !error

  return (
    <Card.Root
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.default"
      width={'100%'}
      height={'full'}
      display={'flex'}
    >
      <Card.Header p={4} pb={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          gap={4}
        >
          <Box>
            <Heading as="h1" fontSize="xl" fontWeight="bold" color="fg.default">
              {t ? t('users.list.title', { defaultValue: 'Users' }) : 'Users'}
            </Heading>
          </Box>
          <HStack
            gap={4}
            justifyContent="flex-end"
            width={{ base: '100%', md: 'auto' }}
            flexShrink={{ base: 1, md: 0 }}
          >
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              t={t}
            />
            {!hideCreateButton &&
              (canCreate ? (
                <Button
                  onClick={handleNewUser}
                  size="md"
                  bg="brand.solid"
                  color="brand.contrast"
                  borderRadius="lg"
                  flexShrink={0}
                  whiteSpace="nowrap"
                  _hover={{ bg: 'brand.emphasized' }}
                  _active={{ bg: 'brand.emphasized' }}
                  css={{
                    '& svg': { stroke: 'var(--chakra-colors-brand-contrast)' },
                  }}
                  px={4}
                  aria-label={
                    t
                      ? t('users.list.newUserAriaLabel', {
                          defaultValue: 'Create new user',
                        })
                      : 'Create new user'
                  }
                  disabled={!isValidPbxId}
                >
                  <Plus size={16} />
                  {t
                    ? t('users.list.newUser', { defaultValue: 'New User' })
                    : 'New User'}
                </Button>
              ) : (
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <Button
                      size="md"
                      bg="brand.solid"
                      color="brand.contrast"
                      borderRadius="lg"
                      flexShrink={0}
                      whiteSpace="nowrap"
                      px={4}
                      disabled
                      aria-label={
                        t
                          ? t('users.list.newUserDisabledAriaLabel', {
                              defaultValue: 'Create new user (disabled)',
                            })
                          : 'Create new user (disabled)'
                      }
                    >
                      <Plus size={16} />
                      {t
                        ? t('users.list.newUser', { defaultValue: 'New User' })
                        : 'New User'}
                    </Button>
                  </Tooltip.Trigger>
                  <Portal>
                    <Tooltip.Positioner>
                      <Tooltip.Content>
                        {t
                          ? t('users.list.newUserPermissionTooltip', {
                              defaultValue:
                                "You don't have permission to create users",
                            })
                          : "You don't have permission to create users"}
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Portal>
                </Tooltip.Root>
              ))}
          </HStack>
        </Box>
      </Card.Header>

      <Card.Body p={4} pt={2}>
        {isLoading && <UsersLoadingState t={t} />}
        {!isLoading && error && (
          <ErrorState message={(error as Error).message} t={t} />
        )}
        {!isLoading && !error && totalCount === 0 && (
          <EmptyState hasSearchTerm={searchTerm.trim().length > 0} t={t} />
        )}
        {!isLoading && !error && pbxUsers.length > 0 && (
          <Box
            overflowX="auto"
            role="region"
            aria-label="Users table"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="lg"
            height={'full'}
            mb={2}
          >
            <Table.Root variant="line" size="sm">
              <Table.Header>
                <Table.Row bg="bg.subtle">
                  {sortableColumns.map((col) =>
                    col.field ? (
                      <SortableColumnHeader
                        key={col.id}
                        label={col.label}
                        field={col.field}
                        sortField={sort.field}
                        direction={sort.direction}
                        onSortChange={handleSort}
                        columnHeaderProps={{
                          scope: 'col',
                          fontSize: 'xs',
                          _hover: { bg: 'bg.emphasized' },
                        }}
                        buttonProps={{
                          fontSize: 'xs',
                          fontWeight: 'bold',
                          color: 'fg.default',
                        }}
                      />
                    ) : (
                      <Table.ColumnHeader
                        key={col.id}
                        px={4}
                        py={3}
                        scope="col"
                        fontSize="xs"
                      >
                        {col.label}
                      </Table.ColumnHeader>
                    ),
                  )}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {pbxUsers.map((pbxUser) => (
                  <UserRow
                    key={pbxUser.id}
                    pbxUser={pbxUser}
                    onNavigateToUser={onNavigateToUser}
                    getUserDetailHref={getUserDetailHref}
                    onDeleteClick={handleDeleteClick}
                    canDelete={canDelete}
                    canEdit={canEdit}
                    onEditClick={onEditClick}
                    getUserEditHref={getUserEditHref}
                    t={t}
                  />
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
        <ListToolbar
          currentPage={currentPage}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) =>
            handlePageSizeChange(size === 'All' ? totalCount : size)
          }
          isLoading={isLoading}
          showToolbar={showPagination}
          showAllOption={false}
          showRecordRange
          t={t}
          i18nPrefix={paginationI18nPrefix}
        />
      </Card.Body>

      <RemoveUserFromPbxDialog
        api={api}
        pbxId={pbxId}
        isOpen={deleteDialogState.isOpen}
        onClose={handleDeleteDialogClose}
        userName={deleteDialogState.userName}
        userId={deleteDialogState.userId}
        onConfirm={handleReassignmentConfirm}
        isProcessing={removeUserMutation.isPending}
        t={t}
      />
    </Card.Root>
  )
}
